import { useState, type FormEvent } from 'react';
import { Button, Group, Modal, Stack, Switch, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { attachUri, type AttachUriBody } from '@/api/objects';
import { brand } from '@/theme/theme';
import { ObjectPreview } from './AttachPreview';
import { useRehearsal } from './useRehearsal';

const isGs = (v: string) => v.startsWith('gs://') && v.length > 5;

/**
 * Staff only: point a pending object at an existing `gs://` blob or prefix (storage-and-uploads.md §9a),
 * and optionally at its already processed data, which readies it at once. "Check" shows what would be
 * attached before anything is.
 */
export function AttachUriModal({ objectId, opened, onClose }: { objectId: string; opened: boolean; onClose: () => void }) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation();
  const [uri, setUri] = useState('');
  const [hasProcessed, setHasProcessed] = useState(false);
  const [processedUri, setProcessedUri] = useState('');
  const qc = useQueryClient();
  const body: AttachUriBody = { uri: uri.trim(), processed_uri: hasProcessed ? processedUri.trim() : '' };
  const r = useRehearsal((dryRun) => attachUri(objectId, body, dryRun), body);

  const handleClose = () => {
    setUri('');
    setHasProcessed(false);
    setProcessedUri('');
    r.reset();
    onClose();
  };
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!r.preview) {
      await r.check();
      return;
    }
    const o = await r.commit();
    if (!o) return;
    qc.setQueryData(['object', objectId], o);
    void qc.invalidateQueries({ queryKey: ['objects'] });
    void qc.invalidateQueries({ queryKey: ['object-sessions', objectId] });
    notifications.show({ color: 'forest', message: t('staff.attached') });
    handleClose();
  };
  const valid = isGs(body.uri) && (!hasProcessed || isGs(body.processed_uri ?? ''));

  return (
    <Modal opened={opened} onClose={handleClose} title={t('staff.attachUriTitle')} size="lg">
      <form onSubmit={(e) => void handleSubmit(e)}>
        <Stack gap="md">
          <Text fz={13} c={brand.textMuted}>
            {t('staff.attachUriHint')}
          </Text>
          <TextInput
            label={t('staff.attachUriLabel')}
            placeholder="gs://bucket/path"
            value={uri}
            onChange={(e) => setUri(e.currentTarget.value)}
            maxLength={512}
            error={r.errors.uri}
            required
            data-autofocus
            ff="monospace"
          />
          <Switch label={t('referenced.hasProcessed')} description={t('referenced.hasProcessedHint')} checked={hasProcessed} onChange={(e) => setHasProcessed(e.currentTarget.checked)} />
          {hasProcessed && (
            <TextInput
              label={t('referenced.processedUri')}
              placeholder="gs://bucket/run/step/"
              value={processedUri}
              onChange={(e) => setProcessedUri(e.currentTarget.value)}
              maxLength={512}
              error={r.errors.processed_uri}
              required
              ff="monospace"
            />
          )}
          {r.preview && <ObjectPreview object={r.preview} />}
          {r.general && (
            <Text c="red" role="alert">
              {r.general}
            </Text>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={handleClose}>
              {tc('common.cancel')}
            </Button>
            {r.preview ? (
              <Button type="submit" loading={r.pending === 'commit'}>
                {t('staff.attach')}
              </Button>
            ) : (
              <Button type="submit" variant="light" loading={r.pending === 'check'} disabled={!valid}>
                {t('referenced.check')}
              </Button>
            )}
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
